import { Mesh, BufferGeometry } from 'three';
import { BIMMaterial, AppearanceAsset } from './MaterialTypes';

export type { BIMMaterial, AppearanceAsset };

export interface Relation {
    type: 'HOST_CONTAINED' | 'HOST_FACE' | 'JOIN' | 'CONSTRAINT' | 'LEVEL_ASSOCIATION';
    targetId: string;
    data?: any;
}

// Constraint interface for element relationships
export interface Constraint {
    type: 'HOST_CONTAINED' | 'HOST_FACE' | 'LEVEL_BASED' | 'ALIGNED' | 'LOCKED' | 'DIMENSIONAL' | 'EQUALITY';
    targetId: string;
    data?: Record<string, unknown>;
}

// ==================== Compound Structures (Architecture) ====================
export type LayerFunction =
    | 'Structure'       // Priority 1
    | 'Substrate'       // Priority 2
    | 'Thermal/Air'     // Priority 3
    | 'Finish 1'        // Priority 4 (Exterior)
    | 'Finish 2'        // Priority 5 (Interior)
    | 'Membrane';       // Priority 0 (Zero thickness)

// Alias for backward compatibility
export type Layer = CompoundStructureLayer;

export interface CompoundStructureLayer {
    function: LayerFunction;
    materialId: string;
    thickness: number; // meters
    wraps: boolean;
    variableThickness?: boolean; // For tapered walls/floors
    deckProfileId?: string; // For structural decks
}

export interface CompoundStructure {
    id: string;
    layers: CompoundStructureLayer[];
    coreLayerIndexFirst: number; // 0-indexed, inclusive
    coreLayerIndexLast: number;  // 0-indexed, inclusive
    endCap: 'Exterior' | 'Interior' | 'None';
    openingWrapping: 'Exterior' | 'Interior' | 'Both' | 'None';
    sampleHeight?: number;
}

export interface StackedWallMember {
    structureId: string; // The sub-structure (Basic Wall Type)
    height: number;      // Height of this segment
    variable: boolean;   // If true, this segment stretches to fill height
    offset: number;      // Horizontal offset from centerline
}

export interface StackedWallStructure {
    id: string;
    members: StackedWallMember[]; // Ordered Bottom to Top
    sampleHeight?: number;
}


// ==================== Extensible Storage (Add-ins) ====================
export interface DataSchema {
    id: string; // GUID
    name: string;
    vendorId: string;
    fields: Record<string, 'String' | 'Number' | 'Boolean' | 'ElementId'>;
}

export interface DataEntity {
    schemaId: string;
    data: Record<string, any>;
}


// Base element interface
export interface Element {
    id: string; // Unique GUID
    name?: string; // Element Name (e.g. "Wall 1")
    numericId?: number; // Integer ID (BIM-like)
    extensibleStorage?: DataEntity[]; // Schema ID -> Data Entity
    category: string;
    type: string; // Family Type Name
    parameters: Record<string, any>;
    geometry?: Mesh | BufferGeometry; // Runtime geometry
    geometryData?: any; // Extended geometry data (beam profiles, duct dimensions, etc.)
    constraints: (Relation | Constraint)[];

    // New 2025: Total Carbon Analysis params
    carbonData?: {
        embodied: number; // kgCO2e
        operational: number; // kgCO2e/yr
    };

    // New 2025: Structural Deep Dive
    structuralMaterial?: StructuralMaterial;
    analyticalModel?: AnalyticalModel;
    rebarHostData?: RebarHostData;

    // Phasing & Design Options
    phaseCreatedId?: string;
    phaseDemolishedId?: string; // null if never demolished
    designOptionId?: string; // null if Main Model

    // Steel fabrication operations applied directly to this element's
    // geometry (BIM Steel tab: Modifier, Cope, Miter, Cut Through).
    steelCuts?: SteelCutData[];
}

// ==================== Structural Deep Dive Types ====================

export interface StructuralMaterial {
    id: string;
    name: string;
    type: 'Concrete' | 'Steel' | 'Wood' | 'Aluminum' | 'Masonry';
    properties: {
        youngsModulus: number; // MPa
        shearModulus: number; // MPa
        poissonRatio: number;
        density: number; // kg/m3
        thermalExpansionCoefficient: number; // 1/K
        yieldStrength?: number; // MPa (Steel)
        concreteCompression?: number; // MPa (Concrete)
    };
}

export interface AnalyticalNode {
    id: string;
    position: [number, number, number];
    boundaryConditions: 'Fixed' | 'Pinned' | 'Roller' | 'Free';
}

export interface AnalyticalCurve {
    startNodeId: string;
    endNodeId: string;
    type: 'Beam' | 'Column' | 'Brace';
}

export interface AnalyticalSurface {
    boundaryNodeIds: string[];
    thickness: number;
    spanDirection: [number, number, number];
}

export interface AnalyticalModel {
    nodes: AnalyticalNode[];
    curves?: AnalyticalCurve[];
    surfaces?: AnalyticalSurface[];
}

export interface RebarHostData {
    validHost: boolean;
    coverSettings: {
        top: number; // mm
        bottom: number; // mm
        sides: number; // mm
    };
    faceCovers?: Record<string, number>; // Face Index -> Cover Value (mm)
}

export interface RebarShapeDefinition {
    id: string; // Shape Code e.g. "00", "11", "21"
    hooks: { start: string; end: string }; // "Standard 90", "Seismic 135"
    segments: {
        lengthFormula?: string; // "A", "B", "A+B"
        direction: [number, number, number];
    }[];
}

export interface RebarElement extends Element {
    category: 'Structural Rebar';
    hostId: string; // ID of Concrete Element
    geometryData: {
        shapeId: string;
        barDiameter: number; // mm
        layoutRule: 'Single' | 'Fixed Number' | 'Max Spacing';
        quantity?: number;
        spacing?: number;
        distributionPath?: [number, number, number][]; // 3D points
    };
}

export interface StructuralConnectionElement extends Element {
    category: 'Structural Connections';
    geometryData: {
        position: [number, number, number];
        connectedElementIds: string[];
        connectionType: 'Moment Frame' | 'Shear' | 'Splice';
    };
    analyticalLinks?: {
        startNodeId: string;
        endNodeId: string;
        stiffness: number; // kNm/rad
    }[];
}

// ==================== Structural Openings Types (BIM Structure > Opening) ====================

/** Host-facing style of an Opening-panel void element. 'Profile' is a free-form
 *  void whose outline was sketched directly on the host wall's face. */
export type StructuralOpeningKind = 'ByFace' | 'Shaft' | 'Wall' | 'Vertical' | 'Cut' | 'Dormer' | 'Profile';

export interface StructuralOpeningGeometryData {
    /** BIM Opening panel flavor this void was created with. */
    kind: StructuralOpeningKind;
    /** Host element id (wall / beam / column / floor). Shaft may omit it. */
    hostId?: string;
    /** Local or world center of the void, meters. */
    center: [number, number, number];
    /** Void box sizes in meters (x = along host length or width, y = thickness, z = height). */
    width: number;
    height: number;
    depth: number;
    /** Rotation (deg) around world Z applied to the void footprint. */
    rotation: number;
    /** Shaft opening: number of levels to cut through from its base. */
    levelsThrough?: number;
    /** Profile opening ('Profile' kind): closed void polygon sketched on the
     *  host wall's vertical face, in the wall's local (u, v) frame — u meters
     *  along the wall run from its start point, v meters above the wall base.
     *  width/height/depth mirror the polygon bounding box so schedules and
     *  the properties palette stay meaningful. */
    profile?: [number, number][];
}

/** A parametric void that subtracts from its structural host (Opening panel). */
export interface StructuralOpeningElement extends Element {
    category: 'Structural Openings';
    geometryData: StructuralOpeningGeometryData;
}

// ==================== Rebar Coupler Type (BIM Structure > Reinforcement) ====================

/** Mechanical rebar splicer placed along a host rebar run (Rebar Coupler tool). */
export interface RebarCouplerGeometryData {
    /** Coupler axis endpoints in world meters. */
    start: [number, number, number];
    end: [number, number, number];
    /** Nominal bar diameter it joins, mm. */
    barDiameter: number;
    hostId?: string;
}

export interface RebarCouplerElement extends Element {
    category: 'Rebar Couplers';
    geometryData: RebarCouplerGeometryData;
}

// ==================== Steel Fabrication Types (BIM Steel tab) ====================

/**
 * A parametric cut applied to a structural member's geometry. Stored on the
 * host element (`steelCuts`) so renderers and takeoff can trim the member.
 */
export interface SteelCutData {
    id: string;
    /** BIM Steel > Parametric Cuts family. */
    kind: 'Modifier' | 'Cope' | 'Miter' | 'CutThrough';
    /** Which end of the member's start->end axis the cut touches. */
    at: 'Start' | 'End' | 'Body';
    /** Cut plane: unit normal + point on plane, world meters (Modifier). */
    plane?: { normal: [number, number, number]; origin: [number, number, number] };
    /** Coping depth/length along member local Z/X axes, meters (Cope). */
    depth?: number;
    length?: number;
    /** Which flange of an I section is relieved (Cope). */
    flange?: 'Top' | 'Bottom' | 'Web';
    /** Miter angle in degrees measured from the member axis (Miter). */
    angle?: number;
    /** Contour cut: 2D polygon hole in member local frame (CutThrough). */
    /** Polygon in [along-axis m, transverse m] pairs, closed loop. */
    contour?: [number, number][];
    /** Element whose geometry drives the cut (Cope/Miter reference). */
    referenceElementId?: string;
    parameters: Record<string, any>;
}

/** Steel Plate (Fabricate Elements > Plate): free-form plate drawn on a work plane. */
export interface SteelPlateGeometryData {
    /** Boundary polygon in the plate's local XY plane, meters. */
    boundary: [number, number][];
    /** Placement plane origin + normal, world meters. */
    origin: [number, number, number];
    normal: [number, number, number];
    /** Local X axis of the plate plane (Y = normal x X), world unit vector. */
    xDirection?: [number, number, number];
    thickness: number; // meters
}

export interface SteelPlateElement extends Element {
    category: 'Steel Plates';
    geometryData: SteelPlateGeometryData;
}

/** A bolt group placed between connected elements (Fabricate Elements > Bolts). */
export interface SteelBoltGeometryData {
    /** World-space position of the first bolt head, meters. */
    position: [number, number, number];
    /** Unit vector along the bolt axis (through the connected plates), world. */
    axis: [number, number, number];
    /** Grid layout vectors in the plane perpendicular to the axis, meters. */
    rowDirection: [number, number, number];
    columnDirection: [number, number, number];
    rowCount: number;
    columnCount: number;
    rowSpacing: number;   // meters
    columnSpacing: number; // meters
    connectedElementIds: string[];
}

export interface SteelBoltElement extends Element {
    category: 'Steel Bolts';
    geometryData: SteelBoltGeometryData;
}

/** A weld seam between two members (Fabricate Elements > Welds). */
export interface SteelWeldGeometryData {
    /** Weld run polyline in world meters. */
    path: [number, number, number][];
    primaryElementId: string;
    secondaryElementId?: string;
    location: 'Shop' | 'Site';
    size: number;     // leg size, meters
    symbol: 'Fillet' | 'Groove' | 'Plug' | 'Slot';
}

export interface SteelWeldElement extends Element {
    category: 'Steel Welds';
    geometryData: SteelWeldGeometryData;
}

// ==================== Precast Fabrication Types (BIM Precast tab) ====================

/** Categories owned by the Precast ribbon tab (tools, renderers, schedules). */
export const PRECAST_CATEGORIES = [
    'Precast Walls',
    'Precast Floors',
    'Precast Columns',
    'Precast Beams',
    'Precast Mounting Parts',
    'Precast Reinforcement',
] as const;

/** True for any element produced or consumed by the Precast toolchain. */
export function isPrecastCategory(category: string): boolean {
    return (PRECAST_CATEGORIES as readonly string[]).includes(category);
}

/** Categories that accept precast mounting parts / reinforcement hosts. */
export const PRECAST_HOST_CATEGORIES = [
    'Walls', 'Floors', 'Structural Foundations',
    'Precast Walls', 'Precast Floors', 'Precast Columns', 'Precast Beams',
] as const;

/** Embedded hardware families from Precast > Connections > Mounting Parts. */
export type PrecastMountingKind =
    | 'Lifting Anchor'
    | 'Handling Loop'
    | 'Insert Plate'
    | 'Bolt Anchor'
    | 'Threaded Insert'
    | 'Shear Key';

export interface PrecastMountingPartGeometryData {
    /** Part origin on/inside the host surface, world meters. */
    position: [number, number, number];
    /** Outward host face normal at placement time, world unit vector. */
    normal: [number, number, number];
    /** In-plane rotation, radians. */
    rotation: number;
    /** Host element id (part is embedded into this member). */
    hostId: string;
    /** Nominal size, mm (plate side / anchor diameter / key depth). */
    size: number;
    kind: PrecastMountingKind;
}

/** A mounting/embedded part placed inside a precast member (Connections). */
export interface PrecastMountingPartElement extends Element {
    category: 'Precast Mounting Parts';
    geometryData: PrecastMountingPartGeometryData;
}

/** One generated bar/mesh wire inside a precast reinforcement element. */
export interface PrecastBarSegment {
    /** Bar axis endpoints, world meters. */
    start: [number, number, number];
    end: [number, number, number];
    /** Nominal diameter, mm. */
    diameter: number;
    /** Bar role for reporting and bar-bending schedules. */
    role: 'vertical' | 'horizontal' | 'transverse' | 'longitudinal' | 'hanger';
}

export interface PrecastWallGeometryData {
    start: [number, number, number];
    end: [number, number, number];
    height: number;      // meters
    thickness: number;   // meters
    baseOffset: number;  // bottom elevation, meters
    /** Joint gap reserved at the panel ends, meters. */
    jointWidth: number;
    /** Monolithic element this panel was split from (shop-drawing lineage). */
    parentElementId?: string;
    panelIndex: number;
    panelCount: number;
    /** Panel mark, e.g. 'WP-1'. */
    mark: string;
}

/** A precast wall panel produced by the Split tool (Split panel). */
export interface PrecastWallElement extends Element {
    category: 'Precast Walls';
    geometryData: PrecastWallGeometryData;
}

export interface PrecastFloorGeometryData {
    /** Panel footprint in world XY, closed polygon, meters. */
    boundary: [number, number][];
    thickness: number;  // meters
    /** Bottom elevation, meters. */
    baseZ: number;
    /** Span / core direction, radians from +X. */
    spanAngle: number;
    hollowCore: boolean;
    /** Hollow-core void pattern (all values meters): transverse count + size. */
    core?: { diameter: number; spacing: number; count: number };
    parentElementId?: string;
    panelIndex: number;
    panelCount: number;
    mark: string;
    jointWidth: number;
}

/** A precast floor slab / hollow-core plank produced by the Split tool. */
export interface PrecastFloorElement extends Element {
    category: 'Precast Floors';
    geometryData: PrecastFloorGeometryData;
}

/** A precast column or beam member (Precast > Structure placement). */
export interface PrecastMemberGeometryData {
    start: [number, number, number];
    end: [number, number, number];
    /** Cross-section width, meters. */
    width: number;
    /** Cross-section depth, meters. */
    depth: number;
    jointWidth: number;
    parentElementId?: string;
    panelIndex: number;
    panelCount: number;
    mark: string;
}

export interface PrecastColumnElement extends Element {
    category: 'Precast Columns';
    geometryData: PrecastMemberGeometryData;
}

export interface PrecastBeamElement extends Element {
    category: 'Precast Beams';
    geometryData: PrecastMemberGeometryData;
}

export interface PrecastReinforcementGeometryData {
    hostId: string;
    hostCategory: string;
    /** Covers in meters, applied from the host faces. */
    coverTop: number;
    coverBottom: number;
    coverSides: number;
    wireDiameter: number; // mm
    spacing: number;      // mm
    /** Face layers: single mesh for hollow-core topping, two for solid. */
    twoFace: boolean;
    bars: PrecastBarSegment[];
}

/** Rule-generated reinforcement mesh placed inside a precast member. */
export interface PrecastReinforcementElement extends Element {
    category: 'Precast Reinforcement';
    geometryData: PrecastReinforcementGeometryData;
}

// Geometry Data Types for Element Reconstruction
export interface HostObjectType {
    id: string; // Type ID
    name: string; // e.g. "Generic - 200mm"
    category: string;
    compoundStructure?: CompoundStructure;
    typeParameters: Record<string, any>;
}

// Geometry Data Types for Element Reconstruction
export type WallJoinType = 'Miter' | 'Butt' | 'SquareOff' | 'None';

export interface WallJoinConfiguration {
    startJoinType: WallJoinType;
    endJoinType: WallJoinType;
    startAllowJoin: boolean;
    endAllowJoin: boolean;
}

export interface WallAttachment {
    targetId: string; // e.g., Roof or Floor ID
    location: 'Top' | 'Bottom';
}

export interface WallGeometryData {
    start: [number, number, number];
    end: [number, number, number];
    height: number; // Actual computed height
    thickness: number; // Total thickness
    layers: CompoundStructureLayer[];
    baseOffset: number;
    baseConstraintId: string; // Level ID
    topConstraintId: string; // Level ID or 'Unconnected'
    topOffset: number;
    locationLine: 'WallCenterline' | 'CoreCenterline' | 'FinishFaceExterior' | 'FinishFaceInterior' | 'CoreFaceExterior' | 'CoreFaceInterior';
    profile?: [number, number][]; // Custom profile for joins (XZ plane - OUTER BOUNDARY)
    layerProfiles?: [number, number][][]; // Individual polygons for each layer (ordered by layer index)
    /** Custom wall shape (BIM "Edit Profile"): closed polygon in the wall's
     *  local (u, v) sketch frame — u meters along the wall run from `start`,
     *  v meters above the wall base. When set, the body renders as this
     *  profile swept across the wall thickness (gable / shaped-top walls) and
     *  the plan-based `profile` / `layerProfiles` extrusion is ignored. */
    elevationProfile?: [number, number][];
    joinConfiguration?: WallJoinConfiguration;
    attachments?: WallAttachment[];
    joinedAtStart?: boolean;
    joinedAtEnd?: boolean;
}

export interface DoorGeometryData {
    hostWallId: string;
    position: number; // 0-1 along wall length
    width: number;
    height: number;
    sillHeight: number;
}

export interface WindowGeometryData {
    hostWallId: string;
    position: number;
    width: number;
    height: number;
    sillHeight: number;
}

export interface DeckProfile {
    name: string;
    ribHeight: number; // mm
    ribWidth: number; // mm
}

export interface SlabShapePoint {
    id: string;
    position: [number, number]; // 2D (x,y)
    offset: number; // Z-offset
    isBoundary: boolean;
}

export interface SlabShapeLine {
    startPointId: string;
    endPointId: string;
    isCrease: boolean;
}

export interface SlabShapeEditorData {
    isEnabled: boolean;
    points: SlabShapePoint[];
    splitLines: SlabShapeLine[];
}

export interface FloorGeometryData {
    boundary: [number, number][]; // 2D polygon points
    thickness: number; // Total thickness
    layers: CompoundStructureLayer[];
    levelId: string;
    heightOffsetFromLevel: number;
    spanDirection?: [number, number, number]; // Normalized vector
    deckProfile?: DeckProfile;
    shapeEditor?: SlabShapeEditorData;
}

export interface RoofGeometryData {
    footprint: [number, number][];
    slope: number; // degrees
    overhang: number;
    baseLevelId: string;
    baseOffsetFromLevel: number;
    roofType: 'Gable' | 'Hip' | 'Flat' | 'Shed';
    definesSlope: boolean[]; // Array matching footprint segments
}

// Extended Element types
export interface WallElement extends Element {
    category: 'Walls';
    geometryData: WallGeometryData;
}

export interface DoorElement extends Element {
    category: 'Doors';
    geometryData: DoorGeometryData;
}

export interface WindowElement extends Element {
    category: 'Windows';
    geometryData: WindowGeometryData;
}

export interface FloorElement extends Element {
    category: 'Floors';
    geometryData: FloorGeometryData;
}

export interface RoofElement extends Element {
    category: 'Roofs';
    geometryData: RoofGeometryData;
}

export interface BeamGeometryData {
    start: [number, number, number];
    end: [number, number, number];
    width: number;
    depth: number;
    rotation: number;
}

export interface BeamElement extends Element {
    category: 'Structural Framing';
    geometryData: BeamGeometryData;
}

export interface TrussGeometryData {
    start: [number, number, number];
    end: [number, number, number];
    height: number;
}

export interface TrussElement extends Element {
    category: 'Structural Trusses';
    geometryData: TrussGeometryData;
}

export interface BraceGeometryData {
    start: [number, number, number];
    end: [number, number, number];
}

export interface BraceElement extends Element {
    category: 'Structural Framing';
    geometryData: BraceGeometryData;
}

export interface ColumnGeometryData {
    location: [number, number, number];
    topOffset: number;
    bottomOffset: number;
    height: number;
    rotation: number;
}

export interface ColumnElement extends Element {
    category: 'Structural Columns';
    geometryData: ColumnGeometryData;
}

export interface DuctFitting {
    id: string;
    type: 'Elbow' | 'Tee' | 'Transition' | 'Takeoff';
    position: [number, number, number];
    angle?: number;
}

export interface DuctGeometryData {
    path: [number, number, number][];
    width: number;
    height: number;
    shape: 'Rectangular' | 'Round' | 'Oval' | 'Flat Oval';
    fittings: DuctFitting[];
}

export interface DuctElement extends Element {
    category: 'Ducts';
    geometryData: DuctGeometryData;
    connectors?: any; // MEPConnector[] or tool-specific formats
}

export interface FlexDuctGeometryData {
    controlPoints: [number, number, number][]; // Spline control points
    diameter: number;
}

export interface FlexDuctElement extends Element {
    category: 'Flex Ducts';
    geometryData: FlexDuctGeometryData;
}

export interface PipeFitting {
    id: string;
    type: 'Elbow' | 'Tee' | 'Reducer' | 'Cap' | 'Valve';
    position: [number, number, number];
    angle?: number;
}

export interface PipeGeometryData {
    path: [number, number, number][];
    diameter: number;
    fittings: PipeFitting[];
    slope: number;
}

export interface PipeElement extends Element {
    category: 'Pipes';
    geometryData: PipeGeometryData;
    connectors?: any; // MEPConnector[] or tool-specific formats
}

export interface FlexPipeGeometryData {
    controlPoints: [number, number, number][];
    diameter: number;
}

export interface FlexPipeElement extends Element {
    category: 'Flex Pipes';
    geometryData: FlexPipeGeometryData;
}

export interface PipePlaceholderGeometryData {
    start: [number, number, number];
    end: [number, number, number];
    estimatedDiameter: number;
}

export interface PipePlaceholderElement extends Element {
    category: 'Pipe Placeholders';
    geometryData: PipePlaceholderGeometryData;
}

export interface PlumbingFixtureGeometryData {
    position: [number, number, number];
    rotation: number;
    width: number;
    depth: number;
    height: number;
}

export interface PlumbingFixtureElement extends Element {
    category: 'Plumbing Fixtures';
    geometryData: PlumbingFixtureGeometryData;
}

export interface SprinklerGeometryData {
    position: [number, number, number];
    faceNormal: [number, number, number];
    radius: number; // For visualization
}

export interface SprinklerGeometryData {
    position: [number, number, number];
    faceNormal: [number, number, number];
    radius: number; // For visualization
}

export interface SprinklerElement extends Element {
    category: 'Sprinklers';
    geometryData: SprinklerGeometryData;
}

export interface FabricationConnector extends MEPConnector {
    connectorType: 'Flanged' | 'Grooved' | 'Plain' | 'Threaded';
    gender?: 'Male' | 'Female' | 'Neutral';
}

export interface FabricationPartGeometryData {
    position: [number, number, number];
    rotation: [number, number, number]; // Euler angles
    cid: number; // Content ID (BIM/Fabrication ID)
    connectors: FabricationConnector[];
    meshPath?: string; // Path to GLB/Geometry if available
}

export interface FabricationPartElement extends Element {
    category: 'Fabrication Parts';
    serviceName: string; // e.g., "Supply Air 2in WG"
    partCid: number;
    geometryData: FabricationPartGeometryData;
}

export interface FabricationService {
    name: string;
    group: string;
    parts: { cid: number; name: string; type: string }[];
}

// --- Insert & Collaborate Types ---

export interface LinkGeometryData {
    linkId: string; // ID of the LinkInstance in LinkManager
    origin: [number, number, number];
    scale: [number, number, number];
    rotation: [number, number, number];
    boxSize?: [number, number, number]; // For box placeholder
}

export interface LinkElement extends Element {
    category: 'BIM Links' | 'Point Cloud' | 'CAD Links' | 'Other Links';
    geometryData: LinkGeometryData;
}



// ==================== Electrical Systems ====================

export interface ElectricalCircuit {
    id: string;
    name: string; // "1", "2,4,6"
    panelId: string;
    load: number; // VA
    rating: number; // Amps (15, 20, etc.)
    elementIds: string[];
    voltage: number; // 120, 208, 277, 480
    poles: number; // 1, 2, 3
}

export interface ElectricalPanelGeometryData {
    position: [number, number, number];
    width: number;
    height: number;
    depth: number;
    rotation: number;
}

export interface ElectricalPanelElement extends Element {
    category: 'Electrical Equipment';
    geometryData: ElectricalPanelGeometryData;
    electricalData: {
        maxMains: number; // Amps
        voltage: number;
        phase: 1 | 3;
        circuits: string[]; // Circuit IDs
    };
}

export interface LightingFixtureGeometryData {
    position: [number, number, number];
    rotation: number;
}

export interface LightingFixtureElement extends Element {
    category: 'Lighting Fixtures';
    geometryData: LightingFixtureGeometryData;
    electricalData: {
        load: number; // VA
        voltage: number;
    };
}

export interface WireGeometryData {
    path: [number, number, number][];
    tickMarks: { position: number; quantity: number }[]; // Position 0-1
}

export interface WireElement extends Element {
    category: 'Wires';
    geometryData: WireGeometryData;
}
export interface GroupGeometryData {
    origin: [number, number, number];
    memberIds: string[]; // IDs of elements in the group
}

export interface GroupElement extends Element {
    category: 'Model Groups';
    geometryData: GroupGeometryData;
}

export interface DecalGeometryData {
    hostId: string;
    position: [number, number, number]; // On face
    normal: [number, number, number]; // Face normal
    width: number;
    height: number;
    imagePath: string;
}

export interface DecalElement extends Element {
    category: 'Decals';
    geometryData: DecalGeometryData;
}

export interface AirTerminalGeometryData {
    position: [number, number, number];
    faceNormal: [number, number, number];
    width: number;
    height: number;
}

export interface AirTerminalElement extends Element {
    category: 'Air Terminals';
    geometryData: AirTerminalGeometryData;
}

export interface MEPConnector {
    id: string;
    position: [number, number, number];
    direction: [number, number, number];
    systemType: 'Supply Air' | 'Return Air' | 'Exhaust Air' | 'Hydronic Supply' | 'Hydronic Return' | 'Sanitary' | 'Domestic Cold Water' | 'Domestic Hot Water' | 'Power' | 'Data';
    size: number; // width/diameter
    shape: 'Round' | 'Rectangular';
    height?: number; // for Rectangular
    connectedTo?: string; // ID of connected element
    flow?: number; // L/s Calculated
}

export interface MEPData {
    flow: number; // L/s
    velocity: number; // m/s
    friction: number; // Pa/m
    pressureDrop: number; // Pa
    systemId?: string;
}

export interface MEPSystem {
    id: string;
    name: string;
    systemType: MEPConnector['systemType'];
    baseEquipmentId?: string; // Pump/Fan
    elements: string[]; // IDs
}

export interface MechanicalEquipmentGeometryData {
    position: [number, number, number];
    rotation: number;
    width: number;
    depth: number;
    height: number;
    connectors: MEPConnector[];
}

export interface MechanicalEquipmentElement extends Element {
    category: 'Mechanical Equipment';
    geometryData: MechanicalEquipmentGeometryData;
}

export interface AreaReinforcementLayer {
    active: boolean;
    barType: string;
    spacing: number; // mm
    hookType?: string;
}

export interface AreaReinforcementGeometryData {
    boundary: [number, number][];
    elevation: number;
    hostId: string;
    layers: {
        topMajor: AreaReinforcementLayer;
        topMinor: AreaReinforcementLayer;
        bottomMajor: AreaReinforcementLayer;
        bottomMinor: AreaReinforcementLayer;
    };
}

export interface AreaReinforcementElement extends Element {
    category: 'Structural Area Reinforcement';
    geometryData: AreaReinforcementGeometryData;
}
export interface CableTrayGeometryData {
    path: [number, number, number][];
    width: number;
    height: number;
}

export interface CableTrayElement extends Element {
    category: 'Cable Trays';
    geometryData: CableTrayGeometryData;
}

export interface ConduitGeometryData {
    path: [number, number, number][];
    diameter: number;
}

export interface ConduitElement extends Element {
    category: 'Conduits';
    geometryData: ConduitGeometryData;
}

export interface CurtainWallGeometryData {
    start: [number, number, number];
    end: [number, number, number];
    height: number;
    verticalGrids: number[];
    horizontalGrids: number[];
}

export interface CurtainWallElement extends Element {
    category: 'Curtain Walls';
    geometryData: CurtainWallGeometryData;
}

// Mass Element for conceptual massing
export interface MassGeometryData {
    footprint: [number, number][]; // Plan polygon (XY), extruded along Z
    height: number;
    elevation: number;
    /** Mass floors generated from the footprint (Show Mass Form and Floors). */
    floors?: { level: number; elevation: number; area: number }[];
    formType?: string; // 'Box', 'Extrusion', 'Cylinder', etc.
    isClosed?: boolean;
}

export interface MassElement extends Element {
    category: 'Mass';
    geometryData: MassGeometryData;
}

// Level Element
export interface LevelGeometryData {
    elevation: number;
    extentStart: [number, number];
    extentEnd: [number, number];
}

export interface LevelElement extends Element {
    category: 'Levels';
    geometryData?: LevelGeometryData;
}



export interface RampGeometryData {
    path: [number, number, number][];
    width: number;
    slope: number; // 1/x ratio or degrees? Usually 1:12
    baseLevelId: string;
    topLevelId: string;
}

export interface RampElement extends Element {
    category: 'Ramps';
    geometryData: RampGeometryData;
}

// ==================== Views & Documentation ====================

export interface ViewRange {
    top: number;   // Offset from Associated Level
    cut: number;   // Cut Plane Offset
    bottom: number; // Bottom Offset
    depth: number;  // View Depth Offset
}

export interface ViewTemplate {
    id: string;
    name: string;
    includeScale: boolean;
    scale: number;
    includeViewRange: boolean;
    viewRange: ViewRange;
    includeModelDisplay: boolean;
    modelDisplay: 'Normal' | 'Halftone' | 'Wireframe';
    // Filters would go here later
}

export interface ViewElement extends Element {
    category: 'Views';
    viewType: 'FloorPlan' | 'CeilingPlan' | 'Section' | 'Elevation' | '3D' | 'Sheet';
    associatedLevelId?: string;
    scale: number; // 1:100 = 100
    viewRange?: ViewRange;
    viewTemplateId?: string;
    cropRegion?: [number, number][]; // 2D polygon
    overrides?: { filterId: string; settings: OverrideGraphicSettings }[];
}

// ==================== Family Editor ====================

export type ReferenceType = 'NotAReference' | 'StrongReference' | 'WeakReference' | 'Left' | 'Right' | 'Front' | 'Back' | 'Bottom' | 'Top' | 'CenterLeftRight' | 'CenterFrontBack';

export interface ReferencePlaneGeometryData {
    origin: [number, number, number];
    normal: [number, number, number]; // Plane normal
    extension: [number, number]; // Length in U and V directions from origin (visual)
    direction: [number, number, number]; // U direction
}

export interface ReferencePlaneElement extends Element {
    category: 'Reference Planes';
    geometryData: ReferencePlaneGeometryData;
    isReference: ReferenceType;
    wallClosure: boolean; // Defines if wall layers wrap at this plane
}

export interface ReferenceLineGeometryData {
    start: [number, number, number];
    end: [number, number, number];
}

export interface ReferenceLineElement extends Element {
    category: 'Reference Lines';
    geometryData: ReferenceLineGeometryData;
    isReference: ReferenceType; // Usually Weak or Strong
}

export interface ProjectLocation {
    id: string;
    name: string;
    siteId: string; // Linked Site Element
    transform: number[]; // 4x4 matrix
}

export interface SiteElement extends Element {
    category: 'Site';
    geometryData: {
        points: [number, number, number][]; // Toposolid points
        surveyPoint: [number, number, number];
        projectBasePoint: [number, number, number];
        angleToTrueNorth: number; // radians
    };
}

export interface OverrideGraphicSettings {
    halftone?: boolean;
    transparency?: number; // 0-100
    projectionLines?: { color?: number; weight?: number; patternId?: string };
    cutLines?: { color?: number; weight?: number; patternId?: string };
    surfacePatterns?: { color?: number; fillPatternId?: string; visible: boolean };
}

export interface FilterRule {
    parameter: string;
    evaluator: 'equals' | 'greater' | 'less' | 'contains' | 'beginsWith';
    value: string | number;
}

export interface ElementFilter {
    id: string;
    name: string;
    categories: string[]; // "Walls", "Doors"
    rules: FilterRule[];
    and: boolean; // True = AND all rules, False = OR
}

export type BIMElement = WallElement | DoorElement | WindowElement | FloorElement | RoofElement | BeamElement | ColumnElement | TrussElement | BraceElement | AreaReinforcementElement | DuctElement | FlexDuctElement | PipeElement | FlexPipeElement | PipePlaceholderElement | CableTrayElement | ConduitElement | AirTerminalElement | MechanicalEquipmentElement | PlumbingFixtureElement | SprinklerElement | FabricationPartElement | LinkElement | GroupElement | DecalElement | CurtainWallElement | MassElement | LevelElement | StairElement | PrecastWallElement | PrecastFloorElement | PrecastColumnElement | PrecastBeamElement | PrecastMountingPartElement | PrecastReinforcementElement | Element;

export interface Family {
    id: string;
    name: string;
    category: string;
    path: string;
    nestedParams: Record<string, any>; // Default type parameters
    instanceParams?: Record<string, any>; // Default instance parameters
    formulas?: Record<string, string>; // Parameter formulas e.g. "Width * 0.5"
    nestedComponents?: {
        name: string; // Component name in this family
        familyId: string; // Reference to another family
        typeId?: string; // Specific type of that family
        instanceParams?: Record<string, string | number>; // Overrides or formula mappings
        shared?: boolean; // If true, creates a separate element in the project
        transform?: number[]; // 4x4 matrix relative to host
    }[];
    geometrySchema?: any; // FamilySchema (JSON)
    symbols: FamilySymbol[];
}

export interface FamilySymbol {
    id: string;
    name: string;
    parameters: Record<string, any>;
}

export type ViewType = 'FloorPlan' | 'CeilingPlan' | 'AreaPlan' | 'Section' | 'Elevation' | 'Callout' | '3D' | 'Schedule' | 'Drafting' | 'Legend' | 'Detail' | 'Drawing';

export interface ViewFilter {
    id: string;
    name: string;
    categories: string[];
    rules: FilterRule[];
}



export interface View {
    id: string;
    name: string;
    type: ViewType;
    discipline?: 'Architectural' | 'Structural' | 'Mechanical' | 'Electrical' | 'Plumbing' | 'Coordination';
    cropRegion?: { x: number; y: number; width: number; height: number };
    scale: number; // e.g., 100 for 1:100
    detailLevel: 'Coarse' | 'Medium' | 'Fine';
    visibility: Record<string, boolean>; // Category visibility map
    filters: ViewFilter[];
    camera?: {
        position: [number, number, number];
        target: [number, number, number];
        projection: 'Perspective' | 'Orthographic';
    };
    // Section/Elevation specific
    cutPlane?: {
        origin: [number, number, number];
        normal: [number, number, number];
    };
    // Plan View Range
    viewRange?: {
        top: number;
        cutPlane: number;
        bottom: number;
        depth: number;
    };
    associatedLevelId?: string; // For plan views
    // Clipping
    clippingPlanes?: {
        normal: [number, number, number];
        constant: number;
    }[];
    sectionBox?: {
        min: [number, number, number];
        max: [number, number, number];
    };
}

export interface Level {
    id: string;
    name: string;
    elevation: number; // meters
    isStory: boolean;
    /** Optional 2D extents override (meters). When absent the datum
     *  auto-fits the model bounds (BIM "3D extents"). Set by the
     *  context tab's Maximize / Propagate / explicit extent edits. */
    extents2D?: { min: [number, number]; max: [number, number] };
}

export interface Grid {
    id: string;
    name: string;
    start: [number, number];
    end: [number, number];
    bubbleEnd: 'Start' | 'End' | 'Both' | 'None';
    /** 3D-locked extents captured at creation so "Reset to 3D Extents"
     *  can restore the datum after 2D drag edits (BIM grids & levels). */
    extents3D?: { start: [number, number]; end: [number, number] };
}

export interface PlacedView {
    id: string;
    viewId: string;
    position: { x: number; y: number };
    viewportType: string;
    scale?: number;
    rotation?: number; // degrees, viewport rotation on the sheet
}

export interface Revision {
    id: string;
    sequence: number;
    date: string;
    description: string;
    issued: boolean;
}

export interface RevisionCloud {
    id: string;
    revisionId: string;
    geometry: [number, number][]; // 2D points
    viewId: string;
}

export type SheetSizeId = 'A0' | 'A1' | 'A2' | 'A3' | 'A4' | 'ARCH_D' | 'ARCH_E' | 'ANSI_B' | 'ANSI_D' | 'ANSI_E';

export interface TitleBlockField {
    id: string;
    label: string; // e.g. 'Project Name', 'Sheet Number'
    value: string;
}

export interface TitleBlock {
    id: string;
    name: string;
    size: SheetSizeId;
    width: number; // mm
    height: number; // mm
    fields: TitleBlockField[];
}

export interface Sheet {
    id: string;
    number: string;
    name: string;
    titleBlockId: string;
    viewsPlaced: PlacedView[];
    revisions: string[]; // Revision IDs
    /** Pre-printed letterhead style id (config/sheetTemplates.ts SheetStyleId). */
    style?: string;
    /** Sheet revision date shown in the title block (ISO date or empty). */
    dateIssued?: string;
    /** Optional notes block rendered on the sheet (BIM-style sheet notes). */
    notes?: string;
}

export interface ScheduleField {
    parameterName: string;
    heading: string;
    width: number;
    hidden: boolean;
    format?: string;
}

export interface Schedule {
    id: string;
    name: string;
    categoryId: string;
    fields: ScheduleField[];
    filters: FilterRule[];
    sorting: { field: string; ascending: boolean }[];
    grouping: { field: string; header: boolean; footer: boolean; blank: boolean }[];
    calculateTotals: boolean;
    itemizeEveryInstance: boolean;
}

export interface Phase {
    id: string;
    name: string;
    sequence: number;
}

export interface DesignOption {
    id: string;
    name: string;
    isPrimary: boolean;
}

export interface DesignOptionSet {
    id: string;
    name: string;
    options: DesignOption[];
    activeOptionId?: string; // Edit mode
}

/**
 * Measurement units inherited from the project template the document was
 * created from (BIM: Manage > Project Units).
 */
export interface UnitSettings {
    system: 'Metric' | 'Imperial';
    lengthUnit: string;
    areaUnit: string;
    volumeUnit: string;
    angleUnit: string;
    lengthPrecision: number;
}

export interface Project {
    id: string;
    name: string;
    number: string;
    client: string;
    address: string;

    /** Template-provided measurement units (see UnitSettings). */
    unitSettings?: UnitSettings;

    // Core Data
    nextElementId: number; // For generating integer IDs
    elements: Map<string, BIMElement>;
    views: Map<string, View>;
    sheets: Map<string, Sheet>;
    schedules: Map<string, Schedule>;
    families: Map<string, Family>;

    /** BIM-like material registry: document-level BIM materials. */
    materials: Map<string, BIMMaterial>;
    /** Appearance assets shared BY REFERENCE between materials (Duplicate vs Duplicate Asset). */
    appearanceAssets: Map<string, AppearanceAsset>;

    // Global Parameters
    globalParameters: Map<string, any>; // Name -> Value

    // Project Organization
    levels: Map<string, Level>;
    grids: Map<string, Grid>;
    phases: Phase[];
    designOptionSets: DesignOptionSet[];
    revisions: Revision[];

    /** User-created paper/title block types (New Sheet dialog > New Paper Type). */
    customTitleBlocks: TitleBlock[];

    // Current State
    activeViewId: string;
    /** Currently open sheet in the viewport (null = no sheet is open). */
    activeSheetId: string | null;
    activePhaseId: string;
}

// Tooling Types
// Keep the canonical uppercase literals in ToolTypeUpper, then allow lowercase
// variants and arbitrary string ids (for plugins/extensions).
export type ToolTypeUpper =
    'SELECT' |
    'WALL' | 'DOOR' | 'WINDOW' | 'FLOOR' | 'ROOF' | 'CEILING' |
    'COLUMN' | 'BEAM' | 'TRUSS' | 'BRACE' | 'BEAM_SYSTEM' | 'STAIR' | 'RAILING' | 'RAMP' |
    'COMPONENT' | 'IN_PLACE_MASS' | 'ROOM' | 'AREA' |
    'FOUNDATION_ISOLATED' | 'FOUNDATION_WALL' | 'FOUNDATION_SLAB' | 'SLAB' |
    'REBAR' | 'AREA_REINFORCEMENT' | 'PATH_REINFORCEMENT' | 'FABRIC_REINFORCEMENT' | 'REBAR_COVER' |
    'REBAR_COUPLER' | 'FABRIC_SHEET' |
    'OPENING_BY_FACE' | 'OPENING_SHAFT' | 'OPENING_WALL' | 'OPENING_VERTICAL' | 'OPENING_CUT' | 'OPENING_DORMER' |
    'WALL_SWEEP' | 'WALL_REVEAL' | 'WALL_BY_FACE' |
    'ROOF_BY_EXTRUSION' | 'ROOF_BY_FACE' | 'SOFFIT' | 'FASCIA' | 'GUTTER' |
    'SLAB_EDGE' | 'FLOOR_BY_FACE' | 'CEILING_BY_FACE' |
    'CURTAIN_GRID' | 'MULLION' | 'CURTAIN_SYSTEM' |
    'ROOM_SEPARATOR' | 'AREA_PLAN' | 'AREA_BOUNDARY' | 'AREA_TAG' |
    'STAIR_BY_SKETCH' | 'RAILING_ON_HOST' | 'COLUMN_ARCH' |
    'MODEL_IN_PLACE' | 'REFERENCE_PLANE' | 'STRUCTURAL_FLOOR' |
    'WORK_PLANE_SET' | 'WORK_PLANE_SHOW' | 'WORK_PLANE_REF' | 'WORK_PLANE_VIEWER' |
    'STEEL_CONNECTION' | 'STEEL_PLATE' | 'STEEL_BOLTS' | 'STEEL_WELDS' |
    'STEEL_MODIFIER' | 'STEEL_COPE' | 'STEEL_MITER' | 'STEEL_CUT_THROUGH' |
    'DUCT' | 'DUCT_PLACEHOLDER' | 'FLEX_DUCT' | 'DUCT_SYSTEM' | 'DUCT_FITTING' | 'DUCT_ACCESSORY' | 'AIR_TERMINAL' | 'MECHANICAL_EQUIPMENT' |
    'PIPE' | 'PIPE_PLACEHOLDER' | 'FLEX_PIPE' | 'PIPE_FITTING' | 'PIPE_ACCESSORY' | 'PLUMBING_FIXTURE' | 'SPRINKLER' |
    'CABLE_TRAY' | 'CABLE_TRAY_FITTING' | 'CONDUIT' | 'WIRE' | 'ELECTRICAL_EQUIPMENT' | 'DEVICE' | 'LIGHTING_FIXTURE' |
    'FABRICATION_PART' |
    'CURTAIN_WALL' |
    'TOPOSOLID' | 'SITE_COMPONENT' | 'PARKING_COMPONENT' | 'PROPERTY_LINE' |
    'IN_PLACE_MASS' | 'PLACE_MASS' |
    'CURTAIN_PANEL_BY_FACE' | 'ROOF_BY_FACE' | 'WALL_BY_FACE' | 'FLOOR_BY_FACE' |
    'BUILDING_PAD' | 'SPLIT_TOPOSOLID' | 'MERGE_SURFACES' | 'GRADED_REGION' | 'SUBDIVIDE_REGION' |
    'MODIFY' | 'ALIGN' | 'OFFSET' | 'MIRROR_PICK' | 'MIRROR_DRAW' | 'MOVE' | 'COPY' | 'ROTATE' | 'SCALE' |
    'TRIM_EXTEND_CORNER' | 'TRIM_EXTEND_SINGLE' | 'TRIM_EXTEND_MULTIPLE' | 'SPLIT_ELEMENT' | 'SPLIT_WITH_GAP' | 'DELETE' |
    'PIN' | 'UNPIN' | 'ARRAY_LINEAR' | 'ARRAY_RADIAL' | 'MATCH_TYPE' |
    'DIMENSION' | 'DIM_LINEAR' | 'DIM_ANGULAR' | 'DIM_RADIAL' | 'DIM_DIAMETER' | 'DIM_ARCLENGTH' |
    'SPOT_ELEVATION' | 'SPOT_COORDINATE' | 'SPOT_SLOPE' |
    'TEXT' | 'TAG' | 'SYMBOL' | 'REVISION_CLOUD' | 'DETAIL_LINE' | 'FILLED_REGION' | 'MASKING_REGION' | 'DETAIL_COMPONENT' | 'REPEATING_DETAIL' | 'INSULATION' |
    'LEVEL' | 'GRID' |
    'VIEW_3D' | 'VIEW_SECTION' | 'VIEW_CALLOUT' | 'VIEW_PLAN' | 'VIEW_ELEVATION' | 'VIEW_DRAFTING' | 'VIEW_LEGEND' | 'VIEW_SCHEDULE' | 'VIEW_SHEET' |
    'SUBREGION' | 'ANALYSIS_ENERGY' |
    'TAG_ROOM' | 'TAG_AREA' | 'TAG_SPACE' | 'TAG_ALL' | 'TAG_MULTI_CATEGORY' | 'TAG_MATERIAL' | 'TAG_MULTI_REBAR' |
    'KEYNOTE' | 'KEYNOTE_ELEMENT' | 'KEYNOTE_MATERIAL' | 'KEYNOTE_USER' |
    'COLOR_FILL_LEGEND' | 'COLOR_FILL_LEGEND_SYSTEMS' | 'MODEL_TEXT' |
    'SWITCH_SYSTEM' |
    'JOIN' | 'UNJOIN' | 'CUT_GEOMETRY' | 'UNCUT_GEOMETRY' |
    'LINEWORK' |
    // Precast tab tools (BIM Precast ribbon)
    'PRECAST_SPLIT' | 'PRECAST_PANEL' | 'PRECAST_MOUNTING_PARTS' | 'PRECAST_REINFORCEMENT' |
    'PRECAST_SHOP_DRAWINGS' | 'PRECAST_CAM_EXPORT' | 'PRECAST_CONFIGURATION' |
    'PRECAST_COLUMN' | 'PRECAST_BEAM';

export type ToolType = ToolTypeUpper | Lowercase<ToolTypeUpper> | (string & {});

export interface ToolProperties {
    // Wall Tool
    wallHeight?: number;
    wallBaseOffset?: number;
    wallTopOffset?: number;
    wallLocationLine?: 'WallCenterline' | 'CoreCenterline' | 'FinishFaceExterior' | 'FinishFaceInterior' | 'CoreFaceExterior' | 'CoreFaceInterior';
    wallChain?: boolean;
    wallBaseConstraint?: string;
    wallTopConstraint?: string;
    wallJoinType?: 'Miter' | 'Butt' | 'SquareOff';

    // Door/Window Tool
    tagOnPlacement?: boolean;

    // Steel Fabrication Tools (BIM Steel tab)
    steelPlateThickness?: number;        // mm
    steelPlateElevation?: number;        // m
    steelBoltStandard?: 'A325' | 'A490' | 'A194' | 'Metric 8.8' | 'Metric 10.9';
    steelBoltDiameter?: number;          // mm
    steelBoltHoleType?: 'Standard' | 'Oversized' | 'Slotted' | 'Countersunk';
    steelBoltRows?: number;
    steelBoltColumns?: number;
    steelBoltSpacing?: number;           // mm
    steelWeldType?: 'Shop' | 'Site';
    steelWeldSize?: number;              // mm
    steelCopeDistance?: number;          // mm coping clearance
    steelCutAngle?: number;              // degrees for planar modifier cuts
    steelCutThroughDiameter?: number;    // mm contour hole diameter

    // Structure tab — Wall (BIM "Structural Wall" variant)
    structuralWall?: boolean;

    // Structure tab — Openings (BIM Opening panel)
    openingWidth?: number;               // mm, rectangular wall/face opening
    openingHeight?: number;              // mm, rectangular wall/face opening
    openingSillOffset?: number;          // mm from host base/level
    shaftDepthLevels?: number;           // number of levels a shaft cuts through

    // Structure tab — Model In-Place
    inPlaceCategory?: string;            // host family category to model in

    // Structure tab — Reinforcement extras
    couplerDiameter?: number;            // mm, rebar coupler bar size
    fabricSheetOverlap?: number;         // mm, fabric sheet lap overlap
    rebarBarDiameter?: number;           // mm, active rebar bar size
    rebarSpacing?: number;               // mm, distribution spacing
    rebarFixedCount?: number;            // bars, Fixed Number layout
    rebarLayoutRule?: 'Single' | 'Fixed Number' | 'Max Spacing';

    // Structure tab — Column placement / style
    columnPlacementMode?: 'Free' | 'AtGrids';
    columnStyle?: 'Vertical' | 'Slanted';

    // Structure tab — Structural Floor (SB)
    structuralFloorThickness?: number;   // mm

    // Structure tab — Beam System layout
    beamSystemRule?: 'FixedDistance' | 'FixedNumber' | 'MaximumSpacing';
    beamSystemDistance?: number;         // mm spacing
    beamSystemCount?: number;            // number of beams
    beamSystemJustification?: 'Beginning' | 'End' | 'Center';

    // Architecture tab — Roof panel
    roofSlopeAngle?: number;             // degrees for footprint/extrusion roofs
    roofOverhang?: number;               // mm eave overhang
    roofComponentKind?: 'Soffit' | 'Fascia' | 'Gutter'; // eave attachment
    // Architecture tab — Wall hosted profile panels
    wallSweepProfile?: 'Cat Grid' | 'Brick Course' | 'Custom'; // sweep/reveal shape
    wallSweepHeight?: number;            // mm offset of the band from wall base
    wallSweepOffset?: number;            // mm protrude (+) / recess (-) depth
    // Architecture tab — Curtain system grid layout
    curtainGridSpacing?: number;         // mm default panel module
    curtainMullionSize?: number;         // mm exposed profile width
    // Architecture tab — Wall by Face conversion
    wallThicknessForByFace?: number;     // m, converted wall thickness

    // Massing & Site tab — Conceptual Mass
    massHeight?: number;                 // m, in-place mass extrusion height
    massFloorSpacing?: number;           // m, mass floor interval (0 = none)
    massFormType?: 'Box' | 'Extrusion' | 'Cylinder' | 'Prism' | 'Wedge'; // Place Mass family

    // Massing & Site tab — Model Site
    toposolidElevation?: number;         // m, top-face sketch elevation
    toposolidDepth?: number;             // m, solid depth below the top face
    buildingPadKind?: 'Cut' | 'Fill';    // excavation vs embankment
    buildingPadElevation?: number;       // m, pad surface elevation
    siteComponentKind?: 'Tree' | 'Shrub' | 'Bench' | 'Lamp' | 'Car'; // RPC element
    parkingSpaceWidth?: number;          // m, bay width
    parkingSpaceDepth?: number;          // m, bay depth
    gradedRegionElevation?: number;      // m, proposed surface offset (cut/fill)
    // Architecture tab — Ceiling placement mode
    ceilingMode?: 'Automatic' | 'Sketch';
    // Architecture tab — Model Text panel
    modelTextSize?: number;              // mm text height
    modelTextDepth?: number;             // mm extrusion depth

    // Annotate tab — Dimension panel
    dimensionTextPosition?: 'Above' | 'Below' | 'Center';
    // Annotate tab — Spot dimensions
    spotElevationType?: 'Absolute' | 'Relative' | 'Shared';
    // Annotate tab — Detail panel
    detailLineStyle?: 'Thin Lines' | 'Medium Lines' | 'Wide Lines' | 'Heavy Lines';
    detailComponentSymbol?: 'Brick' | 'Block' | 'Bolt' | 'Dot';
    filledRegionPattern?: 'Diagonal Crosshatch' | 'ANSI31' | 'ANSI37' | 'Concrete' | 'Insulation' | 'Solid Fill' | (string & {}); // last slot: imported .pat pattern names
    repeatingDetailSpacing?: number;     // mm between repeats
    insulationWidth?: number;            // mm batting width
    // Annotate tab — Tag panel
    tagAllCategories?: boolean;
    keynoteType?: 'Element' | 'Material' | 'User';
    // Annotate tab — Text panel
    textSizeMm?: number;                 // paper text height in mm
    textLeader?: 'None' | 'Left' | 'Right';
    // Annotate tab — Color Fill panel
    colorFillParameter?: string;         // e.g. 'Room Name', 'Department'
    // Annotate tab — Symbol panel
    symbolType?: 'North Arrow' | 'Break Line' | 'Centerline';

    // Analyze tab — Structural Loads panel (BIM Loads dropdown)
    loadCaseName?: string;               // target load case, e.g. 'Dead'
    pointLoadMagnitude?: number;         // N, positive downward
    lineLoadMagnitude?: number;          // N/m along the line
    areaLoadMagnitude?: number;          // N/m² over the boundary polygon

    // Analyze tab — Analytical Model panel
    boundaryConditionType?: 'Fixed' | 'Pinned' | 'Roller' | 'Spring' | 'Free';

    // Analyze tab — Spaces & Zones panel
    spaceHeight?: number;                // m default space height
    spaceName?: string;                  // optional manual space name

    // Precast tab (BIM Precast ribbon)
    precastMountingKind?: 'Lifting Anchor' | 'Handling Loop' | 'Insert Plate' | 'Bolt Anchor' | 'Threaded Insert' | 'Shear Key';
    precastMountingSize?: number;        // mm nominal size of the embedded part
    precastReinfWireDiameter?: number;   // mm welded mesh wire diameter
    precastReinfSpacing?: number;        // mm mesh bar spacing
    precastSplitTargetWidth?: number;    // mm desired panel width (0 = auto by config)

    // Generic
    familyTypeId?: string;
}

export interface ToolState {
    activeTool: ToolType;
    properties: ToolProperties;
    sketchPoints?: [number, number, number][]; // For multi-click tools
}

// Undo/Redo
export interface Command {
    id: string;
    type: string;
    timestamp: number;
    /** Optional human-readable description for undo/redo UI */
    description?: string;
    execute: () => void;
    undo: () => void;
}
// ==================== Vertical Circulation Types ====================

export interface StairRun {
    id: string;
    type?: 'Run'; // Optional for backward compatibility
    style: 'Straight' | 'Spiral' | 'Winder';
    path: [number, number, number][]; // Centerline points
    width: number;
    riserCount: number;
    baseElevation: number;
    topElevation: number;
    beginWithRiser: boolean;
    endWithRiser: boolean;
    shape?: string; // For tool compatibility
    riserHeight?: number; // For tool compatibility
    treadDepth?: number; // For tool compatibility
    geometryData?: any; // For tool compatibility
}

export interface StairLanding {
    id: string;
    type?: 'Landing'; // Optional for backward compatibility
    boundary: [number, number][]; // Polygon
    thickness: number;
    elevation: number;
    shape?: string; // For tool compatibility
    path?: [number, number, number][]; // For tool compatibility
    width?: number; // For tool compatibility
    geometryData?: any; // For tool compatibility
}

export interface StairSupport {
    id: string;
    type: 'Stringer' | 'Carriage';
    location: 'Left' | 'Right' | 'Center';
    width: number;
}

export interface StairGeometryData {
    baseLevelId: string;
    topLevelId: string;
    desiredRiserHeight: number;
    desiredTreadDepth: number;
    actualRiserHeight: number;
    actualTreadDepth: number;
    riserCount: number;

    // Components - support both old and new formats
    runs?: StairRun[];
    landings?: StairLanding[];
    supports?: StairSupport[];
    components?: (StairRun | StairLanding)[]; // For tool compatibility
    width?: number; // For tool compatibility
}

export interface StairElement extends Element {
    category: 'Stairs';
    geometryData: StairGeometryData;
}

export interface RailingGeometryData {
    hostId: string; // Stair or Floor ID
    path: [number, number, number][];
    height: number;
    offsetFromPath: number;
}

export interface RailingElement extends Element {
    category: 'Railings';
    geometryData: RailingGeometryData;
}

// ==================== Structural Loads ====================

export type LoadNature = 'Dead' | 'Live' | 'Wind' | 'Snow' | 'Seismic' | 'Temperature';

export interface LoadCase {
    id: string;
    name: string;
    nature: LoadNature;
    number: number;
}

export interface LoadCombination {
    id: string;
    name: string;
    type: 'Serviceability' | 'Ultimate';
    factors: { caseId: string; factor: number }[]; // e.g. 1.2*D + 1.6*L
}

export interface StructuralLoad extends Element {
    category: 'Structural Loads';
    caseId: string;
    forceVector: [number, number, number]; // N or N/m or N/m2
    momentVector: [number, number, number]; // Nm or Nm/m
}

export interface PointLoad extends StructuralLoad {
    loadType: 'Point';
    position: [number, number, number];
    hostElementId?: string; // e.g. Top of Column
}

export interface LineLoad extends StructuralLoad {
    loadType: 'Line';
    start: [number, number, number];
    end: [number, number, number];
    hostElementId?: string; // e.g. Beam ID
    isProjected: boolean;
}

export interface AreaLoad extends StructuralLoad {
    loadType: 'Area';
    loops: [number, number, number][][]; // Boundary loops
    hostElementId?: string; // e.g. Floor ID
    isProjected: boolean;
}
